<template>
    <div class="CThumbnailImage" :class="{ 'bg-gray-300 dark:bg-subtle': !parsedImageUrl && !dummy }">
        <img
            v-if="dummy"
            class="size-full object-cover"
            loading="lazy"
            src="@/assets/images/dummy-profile-image.jpeg"
            v-bind="$attrs"
        />

        <nuxt-img
            v-else-if="parsedImageUrl"
            :class="imageClass"
            loading="lazy"
            :provider="provider"
            :src="parsedImageUrl"
            v-bind="$attrs"
        />

        <CIcon v-else class="m-auto aspect-square w-1/3 min-w-12" :name="icon" undefined-size />
    </div>
</template>

<script lang="ts">
const icons = {
    project: 'projects',
    job: 'work',
    skill: 'courses',
    org: 'organization',
    profile: 'personal',
    volunteer: 'personal',
    mentor: 'personal',
    talentbase: 'personal',
    talentbase_anonymous: 'anonymous',
    page: 'globe'
} as const

export type ThumbnailType = keyof typeof icons
</script>

<script setup lang="ts">
import { computed } from 'vue'
import CIcon from '@/components/ui/CIcon.vue'
import { useImage } from '@/composables/image/useImage'

const noProvider: ThumbnailType[] = ['page']

interface Props {
    src?: string
    type?: ThumbnailType | string
    dummy?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    src: '',
    type: 'project',
    dummy: false
})

const { parseImgURL } = useImage()
const parsedImageUrl = computed(() => {
    return parseImgURL(props.src)
})

const validType = computed((): ThumbnailType => {
    if (props.type && props.type in icons) {
        return props.type as ThumbnailType
    }
    return 'project'
})

const icon = computed(() => {
    return icons[validType.value]
})

const provider = computed(() => {
    if (noProvider.includes(validType.value)) return undefined
    return 'imagekit'
})

const imageClass = computed(() => [
    'size-full',
    ['job', 'org', 'page'].includes(validType.value) ? 'object-contain p-1' : 'object-cover'
])
</script>
